import React from 'react'

import { IconS24TextAlignedLeft24 } from '@is24/cosma-ui-icons'
import { InsertionFlowScreensProps } from '../../../../../@types/props-types'
import { SCREEN_DESCRIPTION, SCREENS } from '../../../../../services/domain/wizardScreens'
import { DescriptionScreenValidationResult } from '../../../../../@types/validation-types'
import { isEditEntryMode } from '../../../../../@types/types'
import ScreenContainer from '../../../../common/containers/screenContainer/ScreenContainer'
import { ScreenLayout } from '../../../../common/containers/screenLayout/ScreenLayout'
import { useSuggestion } from './useSuggestion'
import { AdDescription } from './fields/adDescription/AdDescription'
import { AdFurnishingNote } from './fields/adFurnishingNote/AdFurnishingNote'
import { AdLocation } from './fields/adLocation/AdLocation'
import { AdOther } from './fields/adOther/AdOther'
import { AdTitle } from '../commonFields/adTitle/AdTitle'
import HomeStreetHome from './fields/homeStreetHome/HomeStreetHome'
import AdOtherFieldBehindTextButton from '../../../../common/buttons/adOtherFieldBehindTextButton/AdOtherFieldBehindTextButton'
import { useAppSelector } from '../../../../../app/hooks'
import { InitState } from '../../../../../redux/reducers/root/initialState'
import { isWebView } from '../../../../../utils/navigation'
import { useTranslation } from 'react-i18next'

const screen = SCREENS[SCREEN_DESCRIPTION]
type AdditionalDescriptionProps = {
  hasFurnishingNote?: boolean
  hasLocationNote?: boolean
  hasOtherNote?: boolean
  hasNoDescriptionNote?: boolean
  hasHomeStreetHome?: boolean
  initialPlaceholder?: string[]
}

const Description: React.FC<
  InsertionFlowScreensProps<DescriptionScreenValidationResult> & AdditionalDescriptionProps
> = ({
  insertionData,
  validationMessages,
  onChange,
  onNavigate,
  hasFurnishingNote = false,
  hasLocationNote = false,
  hasOtherNote = false,
  hasNoDescriptionNote = false,
  hasHomeStreetHome = false,
  flowInformation,
  initialPlaceholder,
  doReset,
}) => {
  const { t } = useTranslation()
  const isMobile = isWebView()

  const entryMode = useAppSelector(
    (state: InitState) => state.insertionFeature.insertionIdentification.metaInformation?.entryMode
  )
  const {
    showTitleSubtitle,
    showDescriptionSubtitle,
    showLocationSubtitle,
    onHandleTitleChange,
    onHandleDescriptionChange,
    onHandleLocationChange,
  } = useSuggestion(flowInformation?.options?.hasTextSuggestions ?? false)

  return (
    <>
      <ScreenContainer
        className={isMobile ? 'margin-bottom-xxl' : ''}
        disableMarginBottom={isMobile}
        containerId={screen.id}
        onNavigate={onNavigate}
        screen={screen}
      >
        <ScreenLayout
          id={`${screen.id}-desk-tooltip`}
          headline={'wizard.screen.description.header'}
          subLine={
            flowInformation?.options?.hasTextSuggestions ? t('wizard.screen.description.mobile.subline') : undefined
          }
          headerIcon={<IconS24TextAlignedLeft24 />}
          screenName={screen.name}
        >
          <div className={isMobile ? 'margin-bottom-m' : ''}>
            <AdTitle
              insertionData={insertionData}
              validationMessages={validationMessages}
              onChange={onChange}
              screenId={screen.id}
              screenName={screen.name}
              showTitleSubtitle={showTitleSubtitle}
              onHandleTitleChange={onHandleTitleChange}
            />
            {!hasNoDescriptionNote && (
              <AdDescription
                insertionData={insertionData}
                validationMessages={validationMessages}
                onChange={onChange}
                screenId={screen.id}
                screenName={screen.name}
                showDescriptionSubtitle={showDescriptionSubtitle}
                onHandleDescriptionChange={onHandleDescriptionChange}
                initialPlaceholder={initialPlaceholder}
              />
            )}

            {!isMobile && hasFurnishingNote && isEditEntryMode(entryMode) && (
              <AdFurnishingNote
                insertionData={insertionData}
                validationMessages={validationMessages}
                onChange={onChange}
                screenName={screen.name}
              />
            )}
            {hasLocationNote && (
              <AdLocation
                insertionData={insertionData}
                validationMessages={validationMessages}
                onChange={onChange}
                screenId={screen.id}
                screenName={screen.name}
                showLocationSubtitle={showLocationSubtitle}
                onHandleLocationChange={onHandleLocationChange}
              />
            )}
            {!isMobile && hasOtherNote && (
              <AdOtherFieldBehindTextButton
                children={
                  <AdOther
                    insertionData={insertionData}
                    validationMessages={validationMessages}
                    onChange={onChange}
                    screenName={screen.name}
                  />
                }
                propertyToWatch='adOther'
                doReset={doReset!}
                onChange={(value) => onChange({ screen: screen.name, property: 'adOther' }, value)}
              />
            )}
            {!isMobile && hasHomeStreetHome && (
              <HomeStreetHome
                insertionData={insertionData}
                onChange={(value) => onChange({ screen: screen.name, property: 'isHomeStreetHomeEnabled' }, value)}
              />
            )}
          </div>
        </ScreenLayout>
      </ScreenContainer>
    </>
  )
}

export default Description
